import { Outlet, useNavigate } from 'react-router-dom'
import { Page } from './page.tsx'
import React, { useEffect, useMemo, useRef } from 'react'
import { newRelic } from '../../utils/new-relic.ts'
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts'
import { selectTheme } from '../../features/setting/setting.slice.ts'
import { Theme } from '../../features/setting/setting.def.ts'
import {
  getDarkComponents,
  getDarkToken,
  getLightComponents,
  getLightToken,
} from '../../styles/antd/config-token/token.ts'
import {
  requestUserInfo,
  selectUser,
  setUserInfo,
} from '../../features/user/user.slice.ts'
import { useLazyGetUserInfoQuery } from '../../apis/auth/auth.api.ts'
import { IdeyaSpinner } from '../components/ideya-spinner.tsx'
import ConfigProvider from 'antd/es/config-provider/index'
import App from 'antd/es/app/index'
import theme from 'antd/es/theme/index'
import {
  getDarkComponents1ppon,
  getDarkToken1ppon,
  getLightComponents1ppon,
  getLightToken1ppon,
} from '../../styles/antd/config-token/ippon-token.ts'
import { isIppon } from '../../utils/helpers.ts'
import { useMatomoTrack } from '../../matomo/use-matomo-track.ts'
import { useFixedBreakpoint } from '../hooks/use-is-mobile.ts'
import { useStaticNotification } from '../antd/use-static-notification.ts'

export const Root: React.FC = () => {
  const navigate = useNavigate()
  const colorTheme = useAppSelector(selectTheme)
  const user = useAppSelector(selectUser)
  const dispatch = useAppDispatch()
  const [getUserInfo, { data: userInfoData, isLoading, isError }] =
    useLazyGetUserInfoQuery()
  // Guards against triggering the /verify probe more than once per mount.
  // Survives StrictMode's setup→cleanup→setup double-invoke in dev and the
  // re-run caused by isLoading flipping after getUserInfo() is dispatched.
  const hasFetchedUserInfo = useRef<boolean>(false)
  useMatomoTrack()
  const screens = useFixedBreakpoint()

  // The antd theme follows colorTheme directly. Deferring it (useDeferredValue)
  // was measured to give no improvement once IdeyaTypography stopped producing a
  // new style object per render, and it made the CSS variables and the antd
  // colours land in two visibly separate stages.
  const token = useMemo(
    () =>
      isIppon
        ? colorTheme === Theme.Dark
          ? getDarkToken1ppon()
          : getLightToken1ppon()
        : colorTheme === Theme.Dark
          ? getDarkToken()
          : getLightToken(),
    [colorTheme]
  )

  const components = useMemo(
    () =>
      isIppon
        ? colorTheme === Theme.Dark
          ? getDarkComponents1ppon()
          : getLightComponents1ppon()
        : colorTheme === Theme.Dark
          ? getDarkComponents()
          : getLightComponents(),
    [colorTheme]
  )

  const antdTheme = useMemo(
    () => ({
      cssVar: true,
      token,
      components,
      algorithm:
        colorTheme === Theme.Dark
          ? theme.darkAlgorithm
          : theme.defaultAlgorithm,
    }),
    [token, components, colorTheme]
  )

  // Fetch user info on mount if user is not present
  useEffect(() => {
    if (!user) {
      // Only issue the /verify probe once; the ref blocks StrictMode's second
      // invoke and the isLoading-driven re-run.
      if (!hasFetchedUserInfo.current) {
        hasFetchedUserInfo.current = true
        dispatch(requestUserInfo())
        getUserInfo()
      }
    } else if (!isLoading) {
      // If already on /idea, preserve search params
      if (location.pathname.startsWith('/idea')) {
        navigate(`/idea${location.search}`)
      } else if (location.pathname === '/') {
        navigate('/idea')
      } else {
        navigate(location.pathname + location.search)
      }
    }
  }, [user, dispatch, getUserInfo, isLoading, navigate])

  useEffect(() => {
    if (userInfoData) {
      dispatch(setUserInfo(userInfoData.data))
    }
  }, [userInfoData, dispatch])

  useEffect(() => {
    if (!user && !isLoading && isError) {
      newRelic.noticeError(new Error('verify failed, redirecting to login'), {
        context: 'rootVerifyToken',
      })
      navigate('/login')
    }
  }, [user, isLoading, isError, navigate])

  if (!user || isLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IdeyaSpinner />
      </div>
    )
  }

  return (
    <ConfigProvider
      theme={antdTheme}
      componentSize={screens.md ? 'small' : 'middle'}
    >
      <App>
        <AppStaticNotificationProvider />
        <div className={'w-full h-full'}>
          <Page userInfo={user}>
            <Outlet />
          </Page>
        </div>
      </App>
    </ConfigProvider>
  )
}

/** Renders nothing — only binds App.useApp() instances to the static holder. */
const AppStaticNotificationProvider: React.FC = () => {
  useStaticNotification()
  return null
}
